import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Star, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ROICalculator } from "../components/ROICalculator";
import { Seo } from "../components/Seo";
import { toast } from "sonner";

const PLANS = [
  { id: "free", name: "Essentiel", tagline: "Pour se lancer et être visible partout.", price: "0€", per: "/ pour toujours",
    features: ["Page foodtruck complète", "Emplacements simplifiés (3 max)", "Présence dans la recherche locale", "Système d'avis clients", "Galerie photo (2 max)", "Horaires et coordonnées"], cta: "Commencer gratuitement", lookup: null },
  { id: "pro", name: "Pro", tagline: "Pour les foodtrucks qui tournent activement.", price: "9€", launch: "6,30€", discount: "-30% à vie", per: "/ mois", featured: true,
    features: ["Tout l'Essentiel, plus :", "Mise en avant emplacement du jour & zone", "Planning multi-emplacements illimité (tournées)", "Statistiques de vues", "Badge « Vérifié »", "Alertes à vos abonnés", "Galerie photo (4 max)"], cta: "Passer en Pro", lookup: "pro_monthly" },
  { id: "proplus", name: "Pro+", tagline: "Pour dominer votre zone de chalandise.", price: "29€", launch: "14,50€", discount: "-50% à vie", per: "/ mois",
    features: ["Tout le Pro, plus :", "Position prioritaire dans la zone", "Mise en avant accueil & national", "Galerie photo illimitée", "Statistiques avancées", "Alertes événements push", "Export des abonnés", "Programme de fidélité clients", "Story Instagram de l'emplacement du jour"], cta: "Passer en Pro+", lookup: "proplus_monthly" },
];

const MATRIX = [
  ["Page foodtruck & avis clients", true, true, true],
  ["Emplacements", "3 max", "Illimité", "Illimité"],
  ["Galerie photo", "2", "4", "Illimité"],
  ["Mise en avant recherche", "-", "Zone", "Prioritaire + national"],
  ["Statistiques", "-", "Vues", "Avancées"],
  ["Badge Vérifié", "-", true, true],
  ["Alertes abonnés", "-", "Emplacement", "Emplacement + événements"],
  ["Export des abonnés", "-", "-", true],
  ["Programme de fidélité", "-", "-", true],
  ["Story Instagram", "-", "-", true],
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(null);
  const [launch, setLaunch] = useState({ remaining: 100, pro_remaining: 100, proplus_remaining: 100 });
  useEffect(() => { api.get("/launch-status").then((r) => setLaunch(r.data)).catch(() => {}); }, []);
  const remain = (id) => (id === "pro" ? (launch.pro_remaining ?? 0) : (launch.proplus_remaining ?? 0));
  const launchActive = (launch.remaining ?? 0) > 0;

  const subscribe = async (plan) => {
    if (!plan.lookup) { nav("/connexion?tab=signup"); return; }
    if (!user) { toast.error("Créez votre compte pour souscrire"); nav(`/connexion?tab=signup&plan=${plan.id}`); return; }
    setLoading(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.lookup, origin_url: window.location.origin, user_id: user.id });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setLoading(null); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
      <Seo title="Tarifs Pro & Pro+ pour foodtrucks" description="Référencez votre foodtruck gratuitement, ou passez en Pro (dès 6,30€/mois) : emplacements illimités, statistiques, alertes abonnés et mise en avant. Tarif de lancement -50%." path="/tarifs" />
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-block bg-street-gold/15 text-street-gold px-3 py-1 rounded-full text-sm font-bold">🚀 Tarif de lancement - jusqu'à -50%</span>
        <h1 className="text-3xl sm:text-5xl font-display font-extrabold text-urban-teal mt-4">Des offres qui suivent votre activité</h1>
        <p className="text-urban-teal/60 mt-3">Commencez gratuitement, à vie. Passez la vitesse supérieure quand la demande explose.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-12 items-start">
        {PLANS.map((p) => (
          <div key={p.id} className={`bg-white rounded-3xl p-7 shadow-warm relative ${p.featured ? "md:scale-105 border-2 border-street-gold shadow-warm-lg" : ""}`} data-testid={`plan-${p.id}`}>
            {p.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-street-gold text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1"><Star size={12} className="fill-white" /> La plus demandée</span>}
            <p className="font-display font-bold text-xl text-urban-teal">{p.name}</p>
            <p className="text-sm text-urban-teal/50 mt-1 h-10">{p.tagline}</p>
            <div className="flex items-end gap-2 mt-3">
              <span className="font-display font-extrabold text-4xl text-urban-teal">{p.launch && launchActive ? p.launch : p.price}</span>
              <span className="text-urban-teal/50 text-sm mb-1">{p.per}</span>
              {p.launch && launchActive && <span className="text-coral-twist line-through text-sm mb-1">{p.price}</span>}
            </div>
            {p.launch && launchActive && <span className="inline-block bg-fresh-mint/15 text-fresh-mint text-xs font-bold px-2 py-0.5 rounded-full mt-1">{p.discount} · {remain(p.id)}/100 places de lancement</span>}
            <ul className="mt-5 space-y-2.5">
              {p.features.map((f, i) => (
                <li key={`${p.id}-${f}`} className={`flex items-start gap-2 text-sm ${i === 0 && p.id !== "free" ? "font-bold text-urban-teal" : "text-urban-teal/70"}`}>
                  {!(i === 0 && p.id !== "free") && <Check size={16} className="text-fresh-mint mt-0.5 shrink-0" />} {f}
                </li>
              ))}
            </ul>
            <Button onClick={() => subscribe(p)} disabled={loading === p.id} className={`w-full rounded-full font-bold mt-6 min-h-12 h-auto py-2 px-3 whitespace-normal leading-tight text-center text-sm sm:text-base ${p.featured ? "bg-street-gold hover:bg-street-gold/90 text-white" : "bg-urban-teal hover:bg-urban-teal/90 text-white"}`} data-testid={`subscribe-${p.id}`}>
              {loading === p.id ? "Redirection..." : p.cta}
            </Button>
          </div>
        ))}
      </div>

      <ROICalculator />

      <div className="mt-14" data-testid="comparison-table">
        <h2 className="text-2xl font-display font-bold text-urban-teal text-center mb-6">Comparatif détaillé</h2>
        <p className="text-xs text-urban-teal/40 mb-2 sm:hidden text-center">← Glissez pour comparer les offres →</p>
        <div className="overflow-x-auto rounded-2xl">
        <table className="w-full min-w-[560px] bg-white rounded-2xl shadow-warm overflow-hidden text-sm">
          <thead className="bg-urban-teal text-white text-left"><tr>
            <th className="p-3 font-bold sticky left-0 bg-urban-teal z-10">Fonctionnalité</th><th className="p-3 font-bold text-center">Essentiel</th><th className="p-3 font-bold text-center">Pro</th><th className="p-3 font-bold text-center">Pro+</th>
          </tr></thead>
          <tbody>
            {MATRIX.map((row, i) => (
              <tr key={row[0]} className={i % 2 ? "bg-cream-bg/40" : ""}>
                <td className="p-3 font-semibold text-urban-teal sticky left-0 bg-white z-10">{row[0]}</td>
                {[1, 2, 3].map((c) => (
                  <td key={c} className="p-3 text-center text-urban-teal/70">
                    {row[c] === true ? <Check size={16} className="text-fresh-mint mx-auto" /> : row[c] === "-" ? <X size={14} className="text-urban-teal/20 mx-auto" /> : row[c]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      <p className="text-center text-urban-teal/40 text-sm mt-8">Paiement sécurisé par Stripe · Carte test : 4242 4242 4242 4242</p>
    </div>
  );
}
